import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getFormValues } from 'redux-form';
import { Checkbox } from 'semantic-ui-react';
import {
    auto_status_bar_icons,
    normalize_status_bar_background,
    resolve_status_bar_contrast_background,
} from '../../../services/statusBarTheme';
import { Utils, Screenings, Events } from '../../../services';
import { first_poster_url } from '../../../services/eventPoster';
import { findEditionById } from '../../../services/editionNavUi';
import { normalize_theme_logos, theme_applies_primary_to_logo } from '../ThemeSettings/ThemeSettings';
import { sanitize_theme_logo_data_uri } from '../../../services/image/sanitizeThemeLogoSvg';
import { subscribe_mobile_preview_boot, subscribe_mobile_preview_screen } from './mobilePreviewBoot';
import { ExploritNavIcon } from '../exploritNavIcon';
import moment from 'moment';

import './MobileAppPreviewDock.css';
import '../exploritIcomoon.scss';

const PREVIEW_VISIBLE_PREF = 'kronos.mobile.previewVisible';
const LEGACY_PREVIEW_VISIBLE_PREF = 'kronos.mobileTheme.previewVisible';

const BOOT_MS_ICON = 1100;
const BOOT_MS_NAME = 1100;
const BOOT_MS_SPLASH = 1700;

const PREVIEW_POSTER_FETCH_LIMIT = 24;
const PREVIEW_POSTER_MAX = 8;

/** Same convention as rn-ticketack-mobile Logo: monochrome SVG tinted with primary. */
function is_svg_data_uri(uri) {
    return typeof uri === 'string' && /^data:image\/svg\+xml/i.test(uri);
}

function ThemeLogoPreview({ uri, primary, className, keepColors }) {
    if (!uri)
        return null;

    const display_uri = sanitize_theme_logo_data_uri(uri) || uri;

    if (is_svg_data_uri(display_uri) && !keepColors) {
        return (
            <span
                className={`${className} ${className}--tintedSvg`}
                style={{
                    backgroundColor: primary || '#000000',
                    WebkitMaskImage: `url("${display_uri}")`,
                    maskImage: `url("${display_uri}")`,
                }}
                aria-hidden
            />
        );
    }

    return <img className={className} src={display_uri} alt="" />;
}
/** Large enough for FeaturedMovie full-bleed; thumbs still look sharp. */
const PREVIEW_POSTER_W = 240;
const PREVIEW_POSTER_H = 360;

/** Nav tab target → settings screens.* key for widget lists. */
const NAV_TARGET_TO_SCREEN = {
    Home: 'home',
    Programme: 'program',
    News: 'news',
    Ticket: 'tickets',
    SteamShop: 'shop',
    Panier: 'cart',
    Favoris: 'favorites',
    Votes: 'ratings',
};

function screen_widgets_for_target(mobile, target) {
    const screen_id = NAV_TARGET_TO_SCREEN[target];
    if (!screen_id)
        return null;
    const widgets = mobile?.screens?.[screen_id]?.widgets;
    return Array.isArray(widgets) && widgets.length ? widgets : null;
}

/** HealthCheckResume is fixed at bottom: 16px; lift dock above it when present. */
const HEALTH_TOAST_SELECTOR = '.HealthCheckResume';
const HEALTH_TOAST_CSS_BOTTOM_PX = 16;
const HEALTH_TOAST_GAP_PX = 12;

function measure_health_toast_dock_bottom_px() {
    const el = typeof document !== 'undefined'
        ? document.querySelector(HEALTH_TOAST_SELECTOR)
        : null;
    if (!el)
        return null;
    const height = el.getBoundingClientRect().height;
    if (!(height > 0))
        return null;
    return Math.ceil(HEALTH_TOAST_CSS_BOTTOM_PX + height + HEALTH_TOAST_GAP_PX);
}

/**
 * Kore applies in_the_future() when no date filter is set — past editions
 * (e.g. GIFF 2025) would return []. Pass edition window to disable that.
 * Edition is optional: cinema instances often have none.
 */
function preview_screening_list_params(edition_id, edition) {
    const params = {
        limit: PREVIEW_POSTER_FETCH_LIMIT,
        fields: ['_id', 'opaque.posters', 'films._id', 'films.id', 'films.opaque.posters', 'films.posters'],
    };
    if (edition_id)
        params.edition = edition_id;
    const start = moment(edition?.event_start_date || edition?.start_date);
    const end = moment(edition?.event_end_date || edition?.end_date);
    if (start.isValid())
        params.stop_at_gte = start.clone().startOf('day').toISOString();
    else
        params.stop_at_gte = moment().subtract(2, 'years').startOf('day').toISOString();
    if (end.isValid())
        params.start_at_lte = end.clone().endOf('day').toISOString();
    return params;
}

function collect_poster_entries_from_screenings(screenings, max = PREVIEW_POSTER_MAX) {
    const entries = [];
    const seen = new Set();
    const push = (id, raw) => {
        if (!raw || seen.has(raw))
            return;
        seen.add(raw);
        entries.push({
            id: id != null ? id : null,
            url: Utils.imgProxyUrl(raw, PREVIEW_POSTER_W, PREVIEW_POSTER_H) || raw,
        });
    };

    for (const screening of screenings || []) {
        const films = Array.isArray(screening?.films) ? screening.films : [];
        for (const film of films) {
            const raw = first_poster_url(film?.opaque?.posters || film?.posters);
            push(film?._id ?? film?.id, raw);
            if (entries.length >= max)
                return entries;
        }
        push(null, first_poster_url(screening?.opaque?.posters));
        if (entries.length >= max)
            return entries;
    }
    return entries;
}

function collect_poster_entries_from_events(events, max = PREVIEW_POSTER_MAX) {
    const entries = [];
    const seen = new Set();
    for (const event of events || []) {
        const raw = first_poster_url(event?.posters);
        if (!raw || seen.has(raw))
            continue;
        seen.add(raw);
        entries.push({
            id: event?.id ?? event?._id ?? null,
            url: Utils.imgProxyUrl(raw, PREVIEW_POSTER_W, PREVIEW_POSTER_H) || raw,
        });
        if (entries.length >= max)
            break;
    }
    return entries;
}

async function fetch_preview_poster_entries(edition_id, edition) {
    try {
        const data = await Screenings.list(preview_screening_list_params(edition_id, edition));
        const from_screenings = collect_poster_entries_from_screenings(data?.screenings);
        if (from_screenings.length)
            return from_screenings;
    } catch (_e) { /* try movies below */ }

    try {
        const data = await Events.list(Utils.EVENT_TYPE_MOVIE, {
            page: 0,
            page_size: PREVIEW_POSTER_FETCH_LIMIT,
            fields: ['id', 'posters'],
        });
        const from_movies = collect_poster_entries_from_events(data?.events);
        if (from_movies.length)
            return from_movies;
    } catch (_e) { /* try generic events below */ }

    try {
        const data = await Events.list(null, {
            page: 0,
            page_size: PREVIEW_POSTER_FETCH_LIMIT,
            fields: ['id', 'posters'],
        });
        return collect_poster_entries_from_events(data?.events);
    } catch (_e) {
        return [];
    }
}

const read_preview_visible = () => {
    try {
        const v = localStorage.getItem(PREVIEW_VISIBLE_PREF)
            ?? localStorage.getItem(LEGACY_PREVIEW_VISIBLE_PREF);
        if (v === '0')
            return false;
        if (v === '1')
            return true;
    } catch (_e) { /* ignore */ }
    return true;
};

const persist_preview_visible = (value) => {
    try {
        localStorage.setItem(PREVIEW_VISIBLE_PREF, value ? '1' : '0');
    } catch (_e) { /* ignore */ }
};

const BootOverlay = ({
    phase,
    appName,
    storeIconUri,
    splashUri,
    logoUri,
    backgroundColor,
    primaryColor,
    keepLogoColors,
}) => {
    if (!phase)
        return null;

    const bg = backgroundColor || '#111111';
    const primary = primaryColor || '#ffffff';

    if (phase === 'icon') {
        return (
            <div className="MobileAppPreview-boot MobileAppPreview-boot--icon">
                <div className="MobileAppPreview-boot-homeGrid" aria-hidden="true" />
                <div className="MobileAppPreview-boot-iconWrap">
                    { storeIconUri ? (
                        <img className="MobileAppPreview-boot-icon" src={ storeIconUri } alt="" />
                    ) : (
                        <span
                            className="MobileAppPreview-boot-iconFallback"
                            style={{ backgroundColor: primary }}
                        />
                    ) }
                    { appName ? (
                        <span className="MobileAppPreview-boot-iconLabel">{ appName }</span>
                    ) : null }
                </div>
            </div>
        );
    }

    if (phase === 'name') {
        return (
            <div className="MobileAppPreview-boot MobileAppPreview-boot--name" style={{ backgroundColor: bg }}>
                <span className="MobileAppPreview-boot-appName">{ appName || '…' }</span>
            </div>
        );
    }

    // splash — mirrors app: splash image contain full screen, else logo 75%×50%
    if (splashUri) {
        return (
            <div
                className="MobileAppPreview-boot MobileAppPreview-boot--splash"
                style={{ backgroundColor: bg }}
            >
                <img
                    className="MobileAppPreview-boot-splashImg"
                    src={ splashUri }
                    alt=""
                />
            </div>
        );
    }

    return (
        <div
            className="MobileAppPreview-boot MobileAppPreview-boot--splash"
            style={{ backgroundColor: bg }}
        >
            { logoUri ? (
                <ThemeLogoPreview
                    className="MobileAppPreview-boot-splashLogo"
                    uri={ logoUri }
                    primary={ primary }
                    keepColors={ keepLogoColors }
                />
            ) : (
                <span
                    className="MobileAppPreview-boot-splashLogoFallback"
                    style={{ backgroundColor: primary }}
                />
            ) }
        </div>
    );
};

/** Strip WYSIWYG HTML to a short plain-text preview line. */
function strip_html_preview(html) {
    if (!html || typeof html !== 'string')
        return '';
    return html
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<\/p>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Home screen: map configured widgets to lightweight mocks.
 * Unknown types fall back to a poster row.
 */

/**
 * Same peek as rn-ticketack-mobile `FEATURED_BELOW_PEEK_PX` (170), scaled to the
 * landscape mock screen (must stay in sync with SCSS mock sizes).
 *
 * App title/synopsis overlays the image — peek is free space *below the image*
 * above the tab bar for the next Home widget. Mock meta must also overlay.
 */
const FEATURED_BELOW_PEEK_PX = 170;
const FEATURED_MIN_H_PX = 200;
const FEATURED_PEEK_REF_WINDOW_H_PX = 768;
const MOCK_LANDSCAPE_OUTER_W_REM = 18;
const MOCK_LANDSCAPE_SCREEN_H_REM = 11.5;
const MOCK_BEZEL_PAD_X_REM = 0.9;
/** Approx. mock tab bar — content viewport is screen minus this. */
const MOCK_LANDSCAPE_NAV_H_REM = 1.25;

/** Stable random Featured poster for the Kronos preview session (like the app). */
let preview_featured_random_url = null;

function reset_preview_featured_random() {
    preview_featured_random_url = null;
}

function is_truthy_setting(value) {
    return value === true || value === 'true' || value === 1 || value === '1';
}

/**
 * @param {object} widget FeaturedMovie widget config
 * @param {{ id?: string|number, url: string }[]} posters
 * @returns {string|null}
 */
function pick_featured_preview_poster(widget, posters) {
    const list = Array.isArray(posters) ? posters.filter((p) => p?.url) : [];
    if (!list.length)
        return null;

    if (is_truthy_setting(widget?.randomMovie)) {
        if (preview_featured_random_url) {
            const kept = list.find((p) => p.url === preview_featured_random_url);
            if (kept)
                return kept.url;
        }
        const picked = list[Math.floor(Math.random() * list.length)];
        preview_featured_random_url = picked.url;
        return picked.url;
    }

    preview_featured_random_url = null;
    const movie_id = widget?.movie;
    if (movie_id != null && String(movie_id).trim() !== '') {
        const found = list.find((p) => String(p.id) === String(movie_id));
        if (found)
            return found.url;
    }
    return list[0].url;
}

/** Featured image box style — landscape caps height so the next widgets peek. */
function featured_mock_image_style(ratio, orientation) {
    const r = Number(ratio) || (9 / 16);
    if (orientation !== 'landscape')
        return { aspectRatio: String(r) };

    const window_h = MOCK_LANDSCAPE_SCREEN_H_REM;
    const content_h = window_h - MOCK_LANDSCAPE_NAV_H_REM;
    const display_w = MOCK_LANDSCAPE_OUTER_W_REM - MOCK_BEZEL_PAD_X_REM;
    const scale = window_h / FEATURED_PEEK_REF_WINDOW_H_PX;
    const peek = FEATURED_BELOW_PEEK_PX * scale;
    const poster_h = display_w / r;
    // Leave `peek` under the image (meta overlays) so the next widget title shows.
    const max_h = Math.max(FEATURED_MIN_H_PX * scale, content_h - peek);
    const display_h = Math.min(poster_h, max_h);

    return {
        width: '100%',
        height: `${display_h}rem`,
        aspectRatio: 'auto',
    };
}

const MockHomeWidgets = ({
    widgets,
    lang,
    primary,
    radius,
    ratio,
    visible,
    rowWidthPct,
    posterUrls,
    onOpenDetail = null,
    thumbs_row,
    fixedNavigation = false,
    orientation = 'portrait',
}) => {
    const poster_at = (i) => {
        const list = Array.isArray(posterUrls) ? posterUrls : [];
        if (!list.length)
            return null;
        const entry = list[i % list.length];
        return typeof entry === 'string' ? entry : (entry?.url || null);
    };

    const poster_entries = () => {
        const list = Array.isArray(posterUrls) ? posterUrls : [];
        return list.map((entry) => (
            typeof entry === 'string' ? { id: null, url: entry } : entry
        )).filter((e) => e?.url);
    };

    const titled_row = (title, key_prefix, { clickable = true } = {}) => (
        <div key={ key_prefix } className="MobileAppPreview-mock-widget">
            { title ? (
                <span className="MobileAppPreview-mock-widgetTitle" style={{ color: primary }}>
                    { title }
                </span>
            ) : null }
            { thumbs_row(visible, key_prefix, rowWidthPct, { clickable }) }
        </div>
    );

    return (
        <div className="MobileAppPreview-mock-widgets">
            { widgets.map((widget, index) => {
                const type = widget?.type;
                const key = `${type || 'w'}-${index}`;

                if (type === 'RichText') {
                    const raw = Utils.localized_or_fallback(widget.text, lang, '');
                    const text = strip_html_preview(raw);
                    return (
                        <div key={ key } className="MobileAppPreview-mock-widget MobileAppPreview-mock-richText">
                            { text ? (
                                <p className="MobileAppPreview-mock-richTextBody">{ text }</p>
                            ) : (
                                <>
                                    <span className="MobileAppPreview-mock-listLine" />
                                    <span className="MobileAppPreview-mock-listLine MobileAppPreview-mock-listLine--short" />
                                </>
                            ) }
                        </div>
                    );
                }

                if (type === 'Section' || type === 'Genre') {
                    const label_map = type === 'Section' ? widget.section : widget.genre;
                    const title = Utils.localized_or_fallback(label_map, lang, '')
                        || (type === 'Section' ? 'Section' : 'Genre');
                    return titled_row(title, key, { clickable: true });
                }

                if (type === 'OnScreenMovies' || type === 'Discover') {
                    const title = Utils.localized_or_fallback(widget.title, lang, '');
                    return titled_row(title, key, { clickable: true });
                }

                if (type === 'FeaturedMovie' || type === 'FeaturedScreening') {
                    const poster = type === 'FeaturedMovie'
                        ? pick_featured_preview_poster(widget, poster_entries())
                        : (poster_at(0) || poster_at(index));
                    const under_chrome = fixedNavigation && index === 0;
                    return (
                        <button
                            key={ key }
                            type="button"
                            className={
                                under_chrome
                                    ? 'MobileAppPreview-mock-widget MobileAppPreview-mock-featuredWidget MobileAppPreview-mock-featuredWidget--underChrome'
                                    : 'MobileAppPreview-mock-widget MobileAppPreview-mock-featuredWidget'
                            }
                            onClick={ () => onOpenDetail?.(poster || null) }
                        >
                            <div
                                className={
                                    orientation === 'landscape'
                                        ? 'MobileAppPreview-mock-featured MobileAppPreview-mock-featured--peek'
                                        : 'MobileAppPreview-mock-featured'
                                }
                                style={ featured_mock_image_style(ratio, orientation) }
                            >
                                { poster ? (
                                    <img
                                        className="MobileAppPreview-mock-featuredImg"
                                        src={ poster }
                                        alt=""
                                        loading="lazy"
                                    />
                                ) : (
                                    <span
                                        className="MobileAppPreview-mock-featuredPlaceholder"
                                        style={{ borderColor: primary }}
                                    />
                                ) }
                                <span className="MobileAppPreview-mock-featuredFade" aria-hidden="true" />
                                <div className="MobileAppPreview-mock-featuredMeta">
                                    <span className="MobileAppPreview-mock-featuredTitle" style={{ color: primary }} />
                                    <span className="MobileAppPreview-mock-featuredSubtitle" style={{ color: primary }} />
                                </div>
                            </div>
                        </button>
                    );
                }

                if (type === 'PostsGrid') {
                    const title = Utils.localized_or_fallback(widget.title, lang, '');
                    const cols = Math.min(4, Math.max(2, Number(widget.nbColumns) || 2));
                    const cells = cols * 2;
                    return (
                        <div key={ key } className="MobileAppPreview-mock-widget">
                            { title ? (
                                <span className="MobileAppPreview-mock-widgetTitle" style={{ color: primary }}>
                                    { title }
                                </span>
                            ) : null }
                            <div
                                className="MobileAppPreview-mock-postsGrid"
                                style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
                            >
                                { Array.from({ length: cells }, (_, i) => {
                                    const poster = poster_at(i + index);
                                    return (
                                        <span
                                            key={ i }
                                            className="MobileAppPreview-mock-postsGridCell"
                                            style={{
                                                borderRadius: `${Math.min(radius, 8)}px`,
                                                borderColor: primary,
                                                backgroundImage: poster ? `url("${poster}")` : undefined,
                                                backgroundColor: poster
                                                    ? undefined
                                                    : (i % 2 === 0
                                                        ? 'rgba(255,255,255,0.18)'
                                                        : 'rgba(255,255,255,0.1)'),
                                                aspectRatio: String(ratio),
                                            }}
                                        />
                                    );
                                }) }
                            </div>
                        </div>
                    );
                }

                if (type === 'NewsList') {
                    const title = Utils.localized_or_fallback(widget.title, lang, '');
                    return (
                        <div key={ key } className="MobileAppPreview-mock-widget">
                            { title ? (
                                <span className="MobileAppPreview-mock-widgetTitle" style={{ color: primary }}>
                                    { title }
                                </span>
                            ) : null }
                            <div className="MobileAppPreview-mock-list">
                                { Array.from({ length: 3 }, (_, i) => (
                                    <div key={ i } className="MobileAppPreview-mock-listRow">
                                        <span
                                            className="MobileAppPreview-mock-listThumb"
                                            style={{
                                                borderRadius: `${Math.min(radius, 6)}px`,
                                                borderColor: primary,
                                                backgroundImage: poster_at(i)
                                                    ? `url("${poster_at(i)}")`
                                                    : undefined,
                                                backgroundColor: poster_at(i)
                                                    ? undefined
                                                    : 'rgba(255,255,255,0.15)',
                                            }}
                                        />
                                        <span className="MobileAppPreview-mock-listLines">
                                            <span className="MobileAppPreview-mock-listLine" />
                                            <span className="MobileAppPreview-mock-listLine MobileAppPreview-mock-listLine--short" />
                                        </span>
                                    </div>
                                )) }
                            </div>
                        </div>
                    );
                }

                if (type === 'Carousel') {
                    const ratio_h = Number(widget.imageHeightRatio);
                    const slide_ratio = Number.isFinite(ratio_h) && ratio_h > 0 ? ratio_h : 0.56;
                    const poster = poster_at(index);
                    return (
                        <div key={ key } className="MobileAppPreview-mock-widget MobileAppPreview-mock-carousel">
                            <div
                                className="MobileAppPreview-mock-carouselSlide"
                                style={{
                                    aspectRatio: String(slide_ratio),
                                    borderRadius: `${Math.min(radius, 10)}px`,
                                    borderColor: primary,
                                    backgroundImage: poster ? `url("${poster}")` : undefined,
                                    backgroundColor: poster ? undefined : 'rgba(255,255,255,0.14)',
                                }}
                            />
                            <div className="MobileAppPreview-mock-carouselDots" aria-hidden="true">
                                { [0, 1, 2].map((dot) => (
                                    <span
                                        key={ dot }
                                        className={
                                            dot === 0
                                                ? 'MobileAppPreview-mock-carouselDot MobileAppPreview-mock-carouselDot--active'
                                                : 'MobileAppPreview-mock-carouselDot'
                                        }
                                    />
                                )) }
                            </div>
                        </div>
                    );
                }

                if (type === 'ProgramList') {
                    const is_portrait = widget.renderer === 'portrait';
                    if (is_portrait) {
                        return (
                            <div key={ key } className="MobileAppPreview-mock-widget MobileAppPreview-mock-programList">
                                { Array.from({ length: 3 }, (_, i) => {
                                    const poster = poster_at(i + index);
                                    return (
                                        <button
                                            key={ i }
                                            type="button"
                                            className="MobileAppPreview-mock-programListRow"
                                            onClick={ () => onOpenDetail?.(poster || null) }
                                        >
                                            <span
                                                className="MobileAppPreview-mock-programListThumb"
                                                style={{
                                                    aspectRatio: String(ratio),
                                                    borderRadius: `${Math.min(radius, 8)}px`,
                                                    borderColor: primary,
                                                    backgroundImage: poster ? `url("${poster}")` : undefined,
                                                    backgroundColor: poster
                                                        ? undefined
                                                        : 'rgba(255,255,255,0.14)',
                                                }}
                                            />
                                            <span className="MobileAppPreview-mock-listLines">
                                                <span className="MobileAppPreview-mock-listLine" />
                                                <span className="MobileAppPreview-mock-listLine MobileAppPreview-mock-listLine--short" />
                                            </span>
                                        </button>
                                    );
                                }) }
                            </div>
                        );
                    }
                    // Landscape: wide horizontal posters (peek next)
                    return (
                        <div key={ key } className="MobileAppPreview-mock-widget">
                            { thumbs_row(Math.min(visible, 3), key, 100, { clickable: true }) }
                        </div>
                    );
                }

                if (type === 'Shop') {
                    return (
                        <div key={ key } className="MobileAppPreview-mock-widget">
                            <div className="MobileAppPreview-mock-shopGrid">
                                { Array.from({ length: 4 }, (_, i) => {
                                    const poster = poster_at(i + index);
                                    return (
                                        <div key={ i } className="MobileAppPreview-mock-shopTile">
                                            <span
                                                className="MobileAppPreview-mock-shopTileImg"
                                                style={{
                                                    borderRadius: `${Math.min(radius, 8)}px`,
                                                    borderColor: primary,
                                                    backgroundImage: poster ? `url("${poster}")` : undefined,
                                                    backgroundColor: poster
                                                        ? undefined
                                                        : (i % 2 === 0
                                                            ? 'rgba(255,255,255,0.18)'
                                                            : 'rgba(255,255,255,0.1)'),
                                                }}
                                            />
                                            <span className="MobileAppPreview-mock-shopTileLabel" />
                                        </div>
                                    );
                                }) }
                            </div>
                        </div>
                    );
                }

                // Unknown — generic poster row
                return (
                    <div key={ key } className="MobileAppPreview-mock-widget">
                        { thumbs_row(Math.min(visible, 4), key, 100, { clickable: true }) }
                    </div>
                );
            }) }
        </div>
    );
};

/** Placeholder body for the selected nav target (not a full screen render). */
const MockScreenBody = ({
    target,
    primary,
    inverse,
    radius,
    ratio,
    visible,
    rowWidthPct,
    posterUrls,
    onOpenDetail = null,
    cartCheckoutLabel = 'Checkout',
    screenWidgets = null,
    lang = 'fr',
    fixedNavigation = false,
    orientation = 'portrait',
}) => {
    const poster_at = (i) => {
        if (!Array.isArray(posterUrls) || !posterUrls.length)
            return null;
        const entry = posterUrls[i % posterUrls.length];
        return typeof entry === 'string' ? entry : (entry?.url || null);
    };

    const thumbs_row = (count, key_prefix, width_pct = rowWidthPct, { clickable = false } = {}) => (
        <div
            className="MobileAppPreview-mock-row"
            style={{ width: `${width_pct}%` }}
        >
            { Array.from({ length: count }, (_, i) => {
                const poster = poster_at(i + (key_prefix === 'b' ? count : 0));
                const thumb_style = {
                    aspectRatio: String(ratio),
                    borderRadius: `${Math.min(radius, 10)}px`,
                    backgroundColor: poster
                        ? 'transparent'
                        : (i % 2 === 0
                            ? 'rgba(255,255,255,0.22)'
                            : 'rgba(255,255,255,0.12)'),
                    borderColor: primary,
                };
                const inner = poster ? (
                    <img
                        className="MobileAppPreview-mock-thumbImg"
                        src={ poster }
                        alt=""
                        loading="lazy"
                    />
                ) : null;
                if (clickable && onOpenDetail) {
                    return (
                        <button
                            key={ `${key_prefix}-${i}` }
                            type="button"
                            className="MobileAppPreview-mock-thumb MobileAppPreview-mock-thumb--btn"
                            style={ thumb_style }
                            onClick={ () => onOpenDetail(poster || null) }
                        >
                            { inner }
                        </button>
                    );
                }
                return (
                    <span
                        key={ `${key_prefix}-${i}` }
                        className="MobileAppPreview-mock-thumb"
                        style={ thumb_style }
                    >
                        { inner }
                    </span>
                );
            }) }
        </div>
    );

    if (Array.isArray(screenWidgets) && screenWidgets.length) {
        return (
            <MockHomeWidgets
                widgets={ screenWidgets }
                lang={ lang }
                primary={ primary }
                radius={ radius }
                ratio={ ratio }
                visible={ visible }
                rowWidthPct={ rowWidthPct }
                posterUrls={ posterUrls }
                onOpenDetail={ onOpenDetail }
                thumbs_row={ thumbs_row }
                fixedNavigation={ fixedNavigation }
                orientation={ orientation }
            />
        );
    }

    if (target === 'Programme') {
        return (
            <>
                { thumbs_row(visible, 'a', 100, { clickable: true }) }
                { thumbs_row(Math.max(2, Math.min(visible, 4)), 'b', 100, { clickable: true }) }
            </>
        );
    }

    if (target === 'News') {
        return (
            <div className="MobileAppPreview-mock-list">
                { Array.from({ length: 4 }, (_, i) => (
                    <div key={ i } className="MobileAppPreview-mock-listRow">
                        <span
                            className="MobileAppPreview-mock-listThumb"
                            style={{
                                borderRadius: `${Math.min(radius, 6)}px`,
                                borderColor: primary,
                                backgroundImage: poster_at(i)
                                    ? `url("${poster_at(i)}")`
                                    : undefined,
                                backgroundColor: poster_at(i)
                                    ? undefined
                                    : 'rgba(255,255,255,0.15)',
                            }}
                        />
                        <span className="MobileAppPreview-mock-listLines">
                            <span className="MobileAppPreview-mock-listLine" />
                            <span className="MobileAppPreview-mock-listLine MobileAppPreview-mock-listLine--short" />
                        </span>
                    </div>
                )) }
            </div>
        );
    }

    if (target === 'Ticket') {
        return (
            <div className="MobileAppPreview-mock-tickets">
                { Array.from({ length: 2 }, (_, i) => (
                    <div
                        key={ i }
                        className="MobileAppPreview-mock-ticket"
                        style={{
                            borderRadius: `${Math.min(radius, 10)}px`,
                            borderColor: primary,
                        }}
                    >
                        <span
                            className="MobileAppPreview-mock-ticketStripe"
                            style={{ backgroundColor: primary }}
                        />
                        <span className="MobileAppPreview-mock-listLine" />
                        <span className="MobileAppPreview-mock-listLine MobileAppPreview-mock-listLine--short" />
                    </div>
                )) }
            </div>
        );
    }

    if (target === 'Favoris') {
        return (
            <div className="MobileAppPreview-mock-list">
                { Array.from({ length: 3 }, (_, i) => {
                    const poster = poster_at(i);
                    return (
                        <button
                            key={ i }
                            type="button"
                            className="MobileAppPreview-mock-listRow MobileAppPreview-mock-listRow--btn"
                            onClick={ () => onOpenDetail?.(poster || null) }
                        >
                            <span
                                className="MobileAppPreview-mock-listThumb"
                                style={{
                                    borderRadius: `${Math.min(radius, 6)}px`,
                                    borderColor: primary,
                                    backgroundImage: poster ? `url("${poster}")` : undefined,
                                    backgroundColor: poster ? undefined : 'rgba(255,255,255,0.15)',
                                }}
                            />
                            <span className="MobileAppPreview-mock-listLines">
                                <span className="MobileAppPreview-mock-listLine" />
                                <span className="MobileAppPreview-mock-listLine MobileAppPreview-mock-listLine--short" />
                            </span>
                            <i className="fa fa-heart MobileAppPreview-mock-favIcon" style={{ color: primary }} />
                        </button>
                    );
                }) }
            </div>
        );
    }

    if (target === 'Panier') {
        return (
            <div className="MobileAppPreview-mock-cart">
                { Array.from({ length: 2 }, (_, i) => (
                    <div key={ i } className="MobileAppPreview-mock-cartRow">
                        <span
                            className="MobileAppPreview-mock-listThumb"
                            style={{
                                borderRadius: `${Math.min(radius, 6)}px`,
                                borderColor: primary,
                                backgroundImage: poster_at(i)
                                    ? `url("${poster_at(i)}")`
                                    : undefined,
                                backgroundColor: poster_at(i)
                                    ? undefined
                                    : 'rgba(255,255,255,0.15)',
                            }}
                        />
                        <span className="MobileAppPreview-mock-listLines">
                            <span className="MobileAppPreview-mock-listLine" />
                            <span className="MobileAppPreview-mock-listLine MobileAppPreview-mock-listLine--short" />
                        </span>
                        <span className="MobileAppPreview-mock-cartQty" style={{ borderColor: primary, color: primary }}>
                            1
                        </span>
                    </div>
                )) }
                <div className="MobileAppPreview-mock-cartTotal">
                    <span className="MobileAppPreview-mock-listLine MobileAppPreview-mock-listLine--short" />
                    <span className="MobileAppPreview-mock-listLine" style={{ width: '35%' }} />
                </div>
                <button
                    type="button"
                    className="MobileAppPreview-mock-btn"
                    tabIndex={ -1 }
                    style={{
                        backgroundColor: primary,
                        color: inverse,
                        borderRadius: `${radius}px`,
                    }}
                >
                    { cartCheckoutLabel }
                </button>
            </div>
        );
    }

    if (target === 'SteamShop' || target === 'Votes') {
        return (
            <>
                { thumbs_row(Math.min(visible, 3), 's', 90) }
                <button
                    type="button"
                    className="MobileAppPreview-mock-btn"
                    tabIndex={ -1 }
                    style={{
                        backgroundColor: primary,
                        color: inverse,
                        borderRadius: `${radius}px`,
                    }}
                >
                    OK
                </button>
            </>
        );
    }

    return (
        <>
            { thumbs_row(visible, 'h', rowWidthPct, { clickable: true }) }
            <button
                type="button"
                className="MobileAppPreview-mock-btn"
                tabIndex={ -1 }
                style={{
                    backgroundColor: primary,
                    color: inverse,
                    borderRadius: `${radius}px`,
                }}
            >
                OK
            </button>
        </>
    );
};

const MockMovieDetail = ({
    poster,
    primary,
    inverse,
    radius,
    onClose,
}) => (
    <div className="MobileAppPreview-mock-movie">
        <button
            type="button"
            className="MobileAppPreview-mock-movieBack"
            onClick={ onClose }
            style={{ color: primary }}
            aria-label="Back"
        >
            <i className="fa fa-chevron-left" />
        </button>
        <div
            className="MobileAppPreview-mock-movieHero"
            style={{
                borderRadius: `${Math.min(radius, 12)}px`,
                borderColor: primary,
                backgroundImage: poster ? `url("${poster}")` : undefined,
                backgroundColor: poster ? undefined : 'rgba(255,255,255,0.12)',
            }}
        />
        <div
            className="MobileAppPreview-mock-moviePanel"
            style={{ borderRadius: `${Math.min(radius, 10)}px` }}
        >
            <span className="MobileAppPreview-mock-listLine" />
            <span className="MobileAppPreview-mock-listLine MobileAppPreview-mock-listLine--short" />
            <span
                className="MobileAppPreview-mock-movieChip"
                style={{ backgroundColor: primary, color: inverse, borderRadius: `${Math.min(radius, 8)}px` }}
            />
            <button
                type="button"
                className="MobileAppPreview-mock-btn"
                tabIndex={ -1 }
                style={{
                    backgroundColor: primary,
                    color: inverse,
                    borderRadius: `${radius}px`,
                    marginTop: '0.35rem',
                }}
            >
                OK
            </button>
        </div>
    </div>
);

/**
 * Full phone mock spanning status bar / content / navigation options.
 * @param {'portrait'|'landscape'} [orientation]
 */
const AppMockPreview = ({
    backgroundColor,
    primaryColor,
    primaryInverse,
    borderRadius,
    imageFormat,
    nbPerRow,
    backgroundImageUri,
    logoUri,
    keepLogoColors = false,
    statusBarStyle,
    statusBarBackground,
    navTabs,
    navColors,
    lang,
    orientation = 'portrait',
    bootPhase = null,
    appName = '',
    storeIconUri = null,
    splashUri = null,
    posterUrls = null,
    activeTarget = null,
    onSelectTarget = null,
    detailPoster = null,
    onOpenDetail = null,
    onCloseDetail = null,
    cartCheckoutLabel = 'Checkout',
    screenWidgets = null,
    fixedNavigation = false,
}) => {
    const is_landscape = orientation === 'landscape';
    const bg = backgroundColor || '#111111';
    const primary = primaryColor || '#ffffff';
    const inverse = primaryInverse || '#000000';
    const radius = Number.isFinite(borderRadius) ? Math.max(0, borderRadius) : 0;
    const n = Number.isFinite(nbPerRow) && nbPerRow > 0 ? nbPerRow : 3;
    const ratio = Number.isFinite(imageFormat) && imageFormat > 0 ? imageFormat : (9 / 16);
    const visible = Math.min(Math.ceil(n), 10);
    const rowWidthPct = (visible / n) * 100;

    const bar_bg = normalize_status_bar_background(statusBarBackground);
    const contrast_bg = resolve_status_bar_contrast_background(bg, statusBarBackground);
    const iconsMode = statusBarStyle === 'auto'
        ? auto_status_bar_icons(contrast_bg)
        : statusBarStyle;
    const statusIconColor = iconsMode === 'dark' ? '#111111' : '#ffffff';

    const screenStyle = {
        backgroundColor: bg,
        ...(backgroundImageUri
            ? {
                backgroundImage: `url("${backgroundImageUri}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center top',
            }
            : {}),
    };

    const tabs = Array.isArray(navTabs) ? navTabs.filter(Boolean) : [];
    const navBg = navColors?.background || '#111111';
    const navBorder = navColors?.border || 'rgba(255,255,255,0.2)';
    const navActive = navColors?.activeColor || '#ffffff';
    const navInactive = navColors?.inactiveColor || '#888888';
    const hide_chrome = !!bootPhase;
    const found_index = tabs.findIndex((tab) => tab?.target === activeTarget);
    const active_index = found_index >= 0 ? found_index : 0;
    const resolved_target = tabs[active_index]?.target || activeTarget || 'Home';
    const fixed_nav = !!fixedNavigation;

    return (
        <div
            className={
                is_landscape
                    ? 'MobileAppPreview-mock MobileAppPreview-mock--landscape'
                    : 'MobileAppPreview-mock MobileAppPreview-mock--portrait'
            }
        >
            <div className="MobileAppPreview-mock-bezel">
                <div
                    className={
                        fixed_nav
                            ? 'MobileAppPreview-mock-screen MobileAppPreview-mock-screen--fixedNav'
                            : 'MobileAppPreview-mock-screen'
                    }
                    style={ screenStyle }
                >
                    { !hide_chrome && (
                        <>
                            { /* Body first when fixed nav so hero sits under overlay chrome */ }
                            <div className="MobileAppPreview-mock-body">
                                { detailPoster !== null ? (
                                    <MockMovieDetail
                                        poster={ detailPoster || null }
                                        primary={ primary }
                                        inverse={ inverse }
                                        radius={ radius }
                                        onClose={ onCloseDetail }
                                    />
                                ) : (
                                    <MockScreenBody
                                        target={ resolved_target }
                                        primary={ primary }
                                        inverse={ inverse }
                                        radius={ radius }
                                        ratio={ ratio }
                                        visible={ visible }
                                        rowWidthPct={ rowWidthPct }
                                        posterUrls={ posterUrls }
                                        onOpenDetail={ onOpenDetail }
                                        cartCheckoutLabel={ cartCheckoutLabel }
                                        screenWidgets={ screenWidgets }
                                        lang={ lang }
                                        fixedNavigation={ fixed_nav }
                                        orientation={ orientation }
                                    />
                                ) }
                            </div>
                            <div className="MobileAppPreview-mock-chrome">
                                <div
                                    className="MobileAppPreview-mock-status"
                                    style={{
                                        color: statusIconColor,
                                        backgroundColor: fixed_nav
                                            ? 'transparent'
                                            : (bar_bg || 'transparent'),
                                    }}
                                >
                                    <span>9:41</span>
                                    <span className="MobileAppPreview-mock-statusIcons">
                                        <i className="fa fa-signal" />
                                        <i className="fa fa-wifi" />
                                        <i className="fa fa-battery-full" />
                                    </span>
                                </div>
                                <div className="MobileAppPreview-mock-header">
                                    { logoUri ? (
                                        <ThemeLogoPreview
                                            className="MobileAppPreview-mock-logoImg"
                                            uri={ logoUri }
                                            primary={ primary }
                                            keepColors={ keepLogoColors }
                                        />
                                    ) : (
                                        <span
                                            className="MobileAppPreview-mock-logo"
                                            style={{
                                                backgroundColor: primary,
                                                borderRadius: `${Math.min(radius, 8)}px`,
                                            }}
                                        />
                                    ) }
                                </div>
                            </div>
                            { tabs.length > 0 && (
                                <div
                                    className="MobileAppPreview-mock-nav"
                                    style={{
                                        backgroundColor: navBg,
                                        borderTopColor: navBorder,
                                    }}
                                >
                                    { tabs.map((tab, i) => {
                                        const color = i === active_index ? navActive : navInactive;
                                        const label = Utils.localized_or_fallback(tab.name, lang) || '';
                                        const target = tab?.target;
                                        return (
                                            <button
                                                key={ i }
                                                type="button"
                                                className="MobileAppPreview-mock-navTab"
                                                style={{ color }}
                                                onClick={ () => {
                                                    if (target && onSelectTarget)
                                                        onSelectTarget(target);
                                                } }
                                                title={ label || target || '' }
                                            >
                                                <ExploritNavIcon icon={ tab.icon } logoUri={ logoUri } />
                                                { label ? (
                                                    <span className="MobileAppPreview-mock-navLabel">
                                                        { label }
                                                    </span>
                                                ) : null }
                                            </button>
                                        );
                                    }) }
                                </div>
                            ) }
                        </>
                    ) }
                    <BootOverlay
                        phase={ bootPhase }
                        appName={ appName }
                        storeIconUri={ storeIconUri }
                        splashUri={ splashUri }
                        logoUri={ logoUri }
                        backgroundColor={ backgroundColor }
                        primaryColor={ primaryColor }
                        keepLogoColors={ keepLogoColors }
                    />
                </div>
            </div>
        </div>
    );
};

const MobileAppPreviewDock = ({ env, setting }) => {
    const { t, i18n } = useTranslation();
    const formValues = useSelector(state => getFormValues('settingForm')(state));
    const reduxScreenings = useSelector(state => state.screenings?.screenings);
    const mobile = formValues?.mobile?.[env] ?? setting?.mobile?.[env];
    const editionId = mobile?.edition || setting?.mobile?.[env]?.edition || null;
    const editionDoc = useSelector(state => findEditionById(state, editionId));
    const theme = mobile?.theme;
    const images = mobile?.images;
    const navTabs = mobile?.navigation?.tabs;

    const [previewVisible, setPreviewVisible] = useState(read_preview_visible);
    const [bootPhase, setBootPhase] = useState(null);
    const [editionPosterUrls, setEditionPosterUrls] = useState([]);
    const [activeTarget, setActiveTarget] = useState(null);
    /** null = list screen; string/'' = movie detail open */
    const [detailPoster, setDetailPoster] = useState(null);
    /** Lift dock above HealthCheckResume when that toast is mounted. */
    const [dockBottomPx, setDockBottomPx] = useState(null);
    const bootTimersRef = useRef([]);
    const splashSeenRef = useRef(undefined);

    const select_preview_target = useCallback((target) => {
        if (!target)
            return;
        setActiveTarget(target);
        setDetailPoster(null);
    }, []);

    const open_movie_detail = useCallback((poster) => {
        setDetailPoster(poster || '');
    }, []);

    const close_movie_detail = useCallback(() => {
        setDetailPoster(null);
    }, []);

    const clear_boot_timers = useCallback(() => {
        bootTimersRef.current.forEach(clearTimeout);
        bootTimersRef.current = [];
    }, []);

    const trigger_boot = useCallback((force = false) => {
        if (!force && !previewVisible)
            return;
        reset_preview_featured_random();
        clear_boot_timers();
        setBootPhase('icon');
        bootTimersRef.current = [
            setTimeout(() => setBootPhase('name'), BOOT_MS_ICON),
            setTimeout(() => setBootPhase('splash'), BOOT_MS_ICON + BOOT_MS_NAME),
            setTimeout(
                () => setBootPhase(null),
                BOOT_MS_ICON + BOOT_MS_NAME + BOOT_MS_SPLASH
            ),
        ];
    }, [clear_boot_timers, previewVisible]);

    useEffect(() => () => clear_boot_timers(), [clear_boot_timers]);

    useEffect(() => subscribe_mobile_preview_boot(() => trigger_boot()), [trigger_boot]);

    useEffect(
        () => subscribe_mobile_preview_screen(select_preview_target),
        [select_preview_target]
    );

    // Keep the floating dock above the shared HealthCheckResume toast.
    useEffect(() => {
        let raf = 0;
        const update = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                setDockBottomPx(measure_health_toast_dock_bottom_px());
            });
        };
        update();
        const mo = new MutationObserver(update);
        mo.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'style'],
        });
        window.addEventListener('resize', update);
        return () => {
            cancelAnimationFrame(raf);
            mo.disconnect();
            window.removeEventListener('resize', update);
        };
    }, []);

    // Keep active tab valid when navigation tabs change.
    useEffect(() => {
        const tabs = Array.isArray(navTabs) ? navTabs.filter(Boolean) : [];
        if (!tabs.length) {
            setActiveTarget(null);
            return;
        }
        if (activeTarget && tabs.some((tab) => tab?.target === activeTarget))
            return;
        setActiveTarget(tabs[0]?.target || null);
    }, [navTabs, activeTarget]);

    // Boot once when entering the mobile settings page (if preview is on).
    useEffect(() => {
        trigger_boot();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- page enter only
    }, []);

    const onPreviewToggle = useCallback((enabled) => {
        setPreviewVisible(enabled);
        persist_preview_visible(enabled);
        if (!enabled) {
            clear_boot_timers();
            setBootPhase(null);
            return;
        }
        trigger_boot(true);
    }, [clear_boot_timers, trigger_boot]);

    const logos = useMemo(() => normalize_theme_logos(theme?.logo), [theme?.logo]);
    const logoUri = useMemo(() => {
        const with_data = logos.find((l) => l?.rfc2397_data);
        return with_data?.rfc2397_data || null;
    }, [logos]);

    const splashUri = theme?.splash?.rfc2397_data
        ?? setting?.mobile?.[env]?.theme?.splash?.rfc2397_data
        ?? null;
    const storeIconUri = mobile?.storeIcon?.rfc2397_data
        ?? setting?.mobile?.[env]?.storeIcon?.rfc2397_data
        ?? null;
    const appName = String(mobile?.appName || '').trim()
        || Utils.localized_or_fallback(setting?.customer?.name, i18n.language)
        || '';

    useEffect(() => {
        if (splashSeenRef.current === undefined) {
            splashSeenRef.current = splashUri;
            return;
        }
        if (splashSeenRef.current === splashUri)
            return;
        splashSeenRef.current = splashUri;
        trigger_boot();
    }, [splashUri, trigger_boot]);

    // Lightweight local fetch — do not replace global screenings redux state.
    // Works without mobile.edition (cinema): screenings window, then movie posters.
    useEffect(() => {
        let cancelled = false;
        fetch_preview_poster_entries(editionId, editionDoc)
            .then((entries) => {
                if (!cancelled)
                    setEditionPosterUrls(entries);
            });
        return () => {
            cancelled = true;
        };
    }, [editionId, editionDoc]);

    const posterUrls = useMemo(() => {
        if (editionPosterUrls.length)
            return editionPosterUrls;
        return collect_poster_entries_from_screenings(reduxScreenings);
    }, [editionPosterUrls, reduxScreenings]);

    const mockProps = {
        backgroundColor: theme?.colors?.background,
        primaryColor: theme?.colors?.primary || '#ffffff',
        primaryInverse: theme?.colors?.['primary-inverse'] || '#000000',
        borderRadius: Number(theme?.style?.borderRadius) || 0,
        imageFormat: Number(images?.format) || (9 / 16),
        nbPerRow: Number(images?.nb_per_row) || 3,
        backgroundImageUri: theme?.backgroundImage?.rfc2397_data
            ?? setting?.mobile?.[env]?.theme?.backgroundImage?.rfc2397_data,
        logoUri,
        keepLogoColors: !theme_applies_primary_to_logo(theme),
        statusBarStyle: theme?.statusBarStyle || 'auto',
        statusBarBackground: theme?.statusBarBackgroundColor,
        navTabs,
        navColors: theme?.colors?.nav,
        lang: i18n.language,
        bootPhase,
        appName,
        storeIconUri,
        splashUri,
        posterUrls,
        activeTarget,
        onSelectTarget: select_preview_target,
        detailPoster,
        onOpenDetail: open_movie_detail,
        onCloseDetail: close_movie_detail,
        cartCheckoutLabel: t('settings.mobile.preview_cart_checkout'),
        screenWidgets: screen_widgets_for_target(mobile, activeTarget || 'Home'),
        fixedNavigation: (() => {
            const screen_id = NAV_TARGET_TO_SCREEN[activeTarget || 'Home'] || 'home';
            const value = mobile?.screens?.[screen_id]?.fixedNavigation;
            return value === true || value === 'true' || value === 1 || value === '1';
        })(),
    };

    if (!setting)
        return null;

    const dockStyle = dockBottomPx != null
        ? { '--MobileAppPreview-dock-bottom': `${dockBottomPx}px` }
        : undefined;

    return (
        <div className="MobileAppPreview-dock" style={ dockStyle }>
            { previewVisible && (
                <div className="MobileAppPreview-mocks">
                    <AppMockPreview orientation="portrait" { ...mockProps } />
                    <AppMockPreview orientation="landscape" { ...mockProps } />
                </div>
            ) }
            <div className="MobileAppPreview-controls">
                { previewVisible && (
                    <button
                        type="button"
                        className="MobileAppPreview-reboot"
                        onClick={ () => trigger_boot(true) }
                        disabled={ !!bootPhase }
                        title={ t('settings.mobile.preview_reboot') }
                        aria-label={ t('settings.mobile.preview_reboot') }
                    >
                        <i className={ `fa ${bootPhase ? 'fa-spinner fa-spin' : 'fa-power-off'}` } />
                    </button>
                ) }
                <div className="MobileAppPreview-toggle">
                    <label className="mb-0 mr-2 text-nowrap" htmlFor="mobile-app-preview-visible">
                        { t('settings.mobile.preview') }
                    </label>
                    <Checkbox
                        toggle
                        id="mobile-app-preview-visible"
                        checked={ previewVisible }
                        onChange={ (_e, { checked }) => onPreviewToggle(!!checked) }
                    />
                </div>
            </div>
        </div>
    );
};

export default MobileAppPreviewDock;
